import { useCallback } from 'react';
import { useDispatch, useSelector } from 'store';

// project imports
import {
  fetchCurrentSubscription,
  fetchCurrentUsage,
  fetchBillingHistory,
  fetchRunningAgents,
  topUpCredits,
  createPaymentIntent,
  createCheckoutSession,
  createCustomerPortalSession,
  subscribeToPlan,
  previewPlanChange,
  changePlan,
  cancelSubscription,
  resetSubscriptionsState,
  selectCurrentSubscription,
  selectCurrentUsage,
  selectBillingHistory,
  selectSubscriptionsStatus,
  selectSubscriptionsError,
  selectRunningAgents,
  selectRunningAgentsStatus,
} from 'store/slices/subscriptions';

// ==============================|| SUBSCRIPTIONS HOOKS ||============================== //

export default function useSubscriptions() {
  const dispatch = useDispatch();

  // Selectors
  const current = useSelector(selectCurrentSubscription);
  const usage = useSelector(selectCurrentUsage);
  const billingHistory = useSelector(selectBillingHistory);
  const status = useSelector(selectSubscriptionsStatus);
  const error = useSelector(selectSubscriptionsError);
  const runningAgents = useSelector(selectRunningAgents);
  const runningAgentsStatus = useSelector(selectRunningAgentsStatus);

  // Computed values from subscription data
  const subscription = current?.subscription || null;
  const plan = current?.plan || null;
  const hasActiveSubscription = current?.hasActiveSubscription ?? (subscription !== null);
  const tierUsage = current?.tierUsage || null;
  const agentCapacity = current?.agentCapacity || null;

  // Price per ACU from system settings (provided by API)
  const pricePerAcu = current?.pricePerAcu;

  // Core values
  const acuAllocation = subscription?.acuAllocation || 0; // Monthly plan allocation
  // currentBalance can be in subscription object OR at root level when no subscription exists
  const currentBalance = subscription?.currentBalance || current?.currentBalance || 0; // Total credits in account (source of truth)

  // Usage calculation from usage API
  const acuUsed = usage?.usage ? usage.usage.totalDeducted : 0;

  // Total available = current remaining balance + what was used
  // (currentBalance is the REMAINING balance after deductions, not the total)
  const totalAvailable = currentBalance + acuUsed;

  // Percentage = how much of total available has been used
  const acuPercentageUsed = totalAvailable > 0 ? (acuUsed / totalAvailable) * 100 : 0;

  // Actions
  const fetchSubscription = useCallback(() => {
    return dispatch(fetchCurrentSubscription());
  }, [dispatch]);

  const fetchUsage = useCallback(() => {
    return dispatch(fetchCurrentUsage());
  }, [dispatch]);

  const fetchBillingHistoryAction = useCallback((organizationId: string) => {
    return dispatch(fetchBillingHistory(organizationId));
  }, [dispatch]);

  const resetState = useCallback(() => {
    dispatch(resetSubscriptionsState());
  }, [dispatch]);

  const topUp = useCallback(async (amountUsd: number, description?: string) => {
    return dispatch(topUpCredits({ amountUsd, description })).unwrap();
  }, [dispatch]);

  // Stripe: Create payment intent for top-ups
  // SECURITY: organizationId now extracted from user context on backend
  const createStripePaymentIntent = useCallback(async (amountUsd: number) => {
    return dispatch(createPaymentIntent({ amountUsd })).unwrap();
  }, [dispatch]);

  // Stripe: Create checkout session for top-ups (redirects to Stripe hosted page)
  // SECURITY: organizationId now extracted from user context on backend
  const createStripeCheckoutSession = useCallback(async (amountUsd: number, successUrl: string, cancelUrl: string) => {
    return dispatch(createCheckoutSession({ amountUsd, successUrl, cancelUrl })).unwrap();
  }, [dispatch]);

  // Stripe: Open Customer Portal
  // SECURITY: organizationId now extracted from user context on backend
  const openCustomerPortal = useCallback(async (returnUrl: string) => {
    return dispatch(createCustomerPortalSession({ returnUrl })).unwrap();
  }, [dispatch]);

  // Stripe: Subscribe to a plan (initial subscription)
  // SECURITY: organizationId now extracted from user context on backend
  const subscribe = useCallback(async (planId: string, successUrl: string, cancelUrl: string, billingPeriod?: 'monthly' | 'annual') => {
    return dispatch(subscribeToPlan({ planId, successUrl, cancelUrl, billingPeriod })).unwrap();
  }, [dispatch]);

  // Stripe: Preview plan change
  // SECURITY: organizationId now extracted from user context on backend
  const previewSubscriptionPlanChange = useCallback(async (newPlanId: string, billingPeriod?: 'monthly' | 'annual') => {
    return dispatch(previewPlanChange({ newPlanId, billingPeriod })).unwrap();
  }, [dispatch]);

  // Stripe: Change subscription plan
  // SECURITY: organizationId now extracted from user context on backend
  const changeSubscriptionPlan = useCallback(async (newPlanId: string, billingPeriod?: 'monthly' | 'annual') => {
    return dispatch(changePlan({ newPlanId, billingPeriod })).unwrap();
  }, [dispatch]);

  // Stripe: Cancel subscription
  // SECURITY: organizationId now extracted from user context on backend
  const cancelSubscriptionPlan = useCallback(async (immediate: boolean = false) => {
    return dispatch(cancelSubscription({ immediate })).unwrap();
  }, [dispatch]);

  const fetchRunningAgentsAction = useCallback(() => {
    return dispatch(fetchRunningAgents());
  }, [dispatch]);

  return {
    // State
    subscription,
    plan,
    hasActiveSubscription,
    usage: usage?.usage || null,
    billingHistory,
    acuAllocation,
    currentBalance,
    totalAvailable,
    acuUsed,
    acuPercentageUsed,
    pricePerAcu,
    tierUsage,
    agentCapacity,
    error,
    isLoading: status === 'loading',
    isSuccess: status === 'success',
    isFailed: status === 'failed',
    isIdle: status === 'idle',

    // Running agents
    runningAgents,
    runningAgentsStatus,

    // Actions
    fetchSubscription,
    fetchUsage,
    fetchBillingHistory: fetchBillingHistoryAction,
    topUp,
    createStripePaymentIntent,
    createStripeCheckoutSession,
    openCustomerPortal,
    subscribe,
    previewSubscriptionPlanChange,
    changeSubscriptionPlan,
    cancelSubscriptionPlan,
    fetchRunningAgents: fetchRunningAgentsAction,
    resetState,
  };
}
